"use client";

import { useUser } from "@clerk/nextjs";
import { IconLoader2 } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useMobileAppData } from "@/api/hooks/use-mobile-app-data";
import { Coupon } from "@/components/coupon";
import { useCartStoreService } from "@/features/cart/hooks/use-cart-store-service";
import type { CartStoreType } from "@/features/cart/types/cart-store-type";
import type { DiscountListItemType } from "@/features/cart/types/discount.type";
import { AddDiscountCouponButton } from "@/features/discounts/components/add-discount-coupon-button";
import { DiscountImage } from "@/features/discounts/components/discount-image";
import { DiscountValidity } from "@/features/discounts/components/discount-validity";
import { NoDiscounts } from "@/features/discounts/components/no-discounts";
import { isMobileDevice } from "@/lib/helpers/is-mobile-device";

export const DiscountsList = ({
  discounts,
  isDiscountListPending = false,
  type,
  isAddDiscountPending = false,
  isRemoveLoading = false,
  setDiscountCode,
  handleRemove,
  handleUseCoupon,
}: {
  discounts: DiscountListItemType[];
  isDiscountListPending?: boolean;
  type?: CartStoreType;
  isAddDiscountPending?: boolean;
  isRemoveLoading?: boolean;
  setDiscountCode?: (code: string | null) => void;
  handleRemove?: () => void;
  handleUseCoupon: (code: string) => void;
}) => {
  const { isSignedIn } = useUser();

  const t = useTranslations();

  const { cartStore } = useCartStoreService({ type });
  const { data: mobileAppData } = useMobileAppData();
  const handleUseCouponWithMobileApp = (
    code: string,
    isMobileCode: boolean,
  ) => {
    if (isMobileCode && mobileAppData?.data?.appLink) {
      if (isMobileDevice()) {
        window.open(mobileAppData.data?.appLink, "_blank");
      }
      return;
    }
    handleUseCoupon(code);
  };

  return (
    <div>
      {isDiscountListPending && (
        <div className="flex items-center justify-center p-4">
          <IconLoader2 className="h-4 w-4 animate-spin" />
        </div>
      )}
      <div className="flex h-full flex-col gap-4 md:gap-6">
        {discounts.length === 0 && isSignedIn && !isDiscountListPending && (
          <NoDiscounts />
        )}

        {discounts.length > 0 &&
          discounts?.map((discount) => (
            <Coupon
              description={
                discount.code === "NOWY15"
                  ? t("discounts.discountNewCodeAlert.description")
                  : discount.description
              }
              footer={<DiscountValidity validity={discount.validity} />}
              image={
                <DiscountImage
                  image={discount.image || discount.discount.type}
                />
              }
              isActive={cartStore.discountCode === discount.code}
              key={discount.id}
              title={discount.title}
            >
              <AddDiscountCouponButton
                discount={discount}
                isActive={cartStore.discountCode === discount.code}
                isDisabled={
                  cartStore.discountCode === discount.code &&
                  (isAddDiscountPending || isRemoveLoading)
                }
                isLoading={isAddDiscountPending || isRemoveLoading}
                mobileAppData={mobileAppData?.data}
                onRemove={() => {
                  setDiscountCode?.(null);
                  handleRemove?.();
                }}
                onUse={(onlyMobileApp: boolean) =>
                  handleUseCouponWithMobileApp(discount.code, onlyMobileApp)
                }
              />
            </Coupon>
          ))}
      </div>
    </div>
  );
};
